<template>
  <div class="page-wrapper">
    <div class="container-fluid px-0 py-0">
      <div class="main-card shadow-sm">
        <div
          class="card-header-custom border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3"
        >
          <div class="d-flex align-items-center gap-3">
            <h1 class="h4 mb-0 fw-bold text-dark">Gestión de Permisos</h1>
            <span class="badge bg-light text-dark border shadow-sm">
              <i class="fas fa-users text-primary me-1"></i>
              Total: {{ totalUsers }}
            </span>
          </div>
          <div>
            <button
              class="btn btn-primary btn-sm shadow-sm d-flex align-items-center"
              @click="openModalImportar"
            >
              <i class="fas fa-plus-circle me-2"></i> Nuevo Usuario
            </button>
          </div>
        </div>

        <div class="p-4 bg-light border-bottom">
          <div class="row">
            <div class="col-md-5">
              <SearchInput v-model="searchTerm" class="w-100" />
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div v-if="!loading && users.length === 0" class="text-center py-5">
            <div class="text-secondary mb-3">
              <i class="fas fa-users-slash fa-3x opacity-50"></i>
            </div>
            <p class="text-muted">No se encontraron usuarios.</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead class="bg-light text-secondary text-uppercase small">
                <tr>
                  <th class="ps-4 py-1 border-0">Usuario</th>
                  <th class="py-1 border-0">Datos de Contacto</th>
                  <th class="pe-4 py-1 border-0 text-end">Permisos</th>
                </tr>
              </thead>
              <tbody>
                <TableSpinner
                  v-if="loading"
                  :colspan="3"
                  text="Cargando usuarios..."
                />

                <template v-else>
                  <tr v-for="user in users" :key="user.id">
                    <td class="ps-4 py-1">
                      <div class="d-flex align-items-center">
                        <div
                          class="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center mr-3"
                          style="
                            width: 40px;
                            height: 40px;
                            background-color: #e7f1ff;
                            color: #0d6efd;
                            font-weight: bold;
                          "
                        >
                          {{
                            user.name ? user.name.charAt(0).toUpperCase() : "U"
                          }}
                        </div>
                        <div class="d-flex flex-column">
                          <span class="fw-bold text-dark"
                            >{{ user.lastname }}, {{ user.name }}</span
                          >
                          <small class="text-muted text-monospace">
                            <i class="fas fa-id-card mr-1"></i>
                            {{ user.dni || "Sin DNI" }}
                          </small>
                        </div>
                      </div>
                    </td>

                    <td class="py-1">
                      <div class="d-flex flex-column small">
                        <span class="text-dark mb-1">
                          <i class="fas fa-envelope text-muted me-2"></i>
                          {{ user.email || "N/A" }}
                        </span>
                        <span class="text-muted text-monospace">
                          <i class="fas fa-briefcase text-muted me-2"></i> CUIT:
                          {{ user.cuit || "N/A" }}
                        </span>
                      </div>
                    </td>

                    <td class="pe-4 py-1 text-end">
                      <ActionDropdown>
                        <button
                          class="dropdown-item d-flex align-items-center"
                          @click="openViewsModal(user)"
                        >
                          <i class="fas fa-eye text-primary fa-fw me-2"></i>
                          Gestión de Vistas
                        </button>
                        <button
                          class="dropdown-item d-flex align-items-center"
                          @click="openCircularModal(user)"
                        >
                          <i class="fas fa-ring text-info fa-fw me-2"></i>
                          Gestión de Circulares
                        </button>
                      </ActionDropdown>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div
          class="card-footer-custom p-3 bg-light border-top d-flex justify-content-end"
        >
          <DataPagination
            :current-page="currentPage"
            :last-page="lastPage"
            @fetch-views="loadUsers"
          />
        </div>
      </div>
    </div>

    <ModalPermisosVistas
      :isOpen="modalOpen"
      :user="selectedUser"
      @close="handleModalClose"
      @success="handleModalSuccess"
    />
    <ModalPermisosCirculares
      :isOpen="modalOpenCirculares"
      :user="selectedUser"
      @close="handleModalClose"
      @success="handleModalSuccess"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import { debounce } from "lodash";
import { fetchUsers } from "@/services/api/concessionaire-user";

import SearchInput from "@/components/data-display/SearchInput.vue";
import DataPagination from "@/components/data-display/DataPagination.vue";
import ModalPermisosVistas from "@/components/permisos/ModalPermisosVistas.vue";
import ModalPermisosCirculares from "@/components/permisos/ModalPermisosCirculares.vue";

// CAMBIO: Se reemplaza LoadingSpinner por TableSpinner
import TableSpinner from "@/components/ui/TableSpinner.vue";
import ActionDropdown from "@/components/common/ActionDropdown.vue";

import Swal from "sweetalert2";

export default {
  name: "PermisosView",
  components: {
    SearchInput,
    DataPagination,
    ModalPermisosVistas,
    ModalPermisosCirculares,
    TableSpinner, // Registrado
    ActionDropdown,
  },
  setup() {
    const users = ref([]);
    const searchTerm = ref("");
    const currentPage = ref(1);
    const lastPage = ref(1);
    const totalUsers = ref(0);
    const loading = ref(false);
    const modalOpen = ref(false);
    const modalOpenCirculares = ref(false);
    const selectedUser = ref(null);

    const loadUsers = async (page = 1) => {
      loading.value = true;
      currentPage.value = page;
      try {
        const response = await fetchUsers({
          page: currentPage.value,
          search: searchTerm.value,
          perPage: 10,
        });
        users.value = response.data;
        currentPage.value = response.meta.current_page;
        lastPage.value = response.meta.last_page;
        totalUsers.value = response.meta.total;
      } catch (error) {
        console.error("Error al cargar usuarios:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar los usuarios.",
        });
      } finally {
        loading.value = false;
      }
    };

    const openViewsModal = (user) => {
      selectedUser.value = user;
      modalOpen.value = true;
    };

    const openCircularModal = (user) => {
      selectedUser.value = user;
      modalOpenCirculares.value = true;
    };

    const handleModalClose = () => {
      modalOpen.value = false;
      modalOpenCirculares.value = false;
    };

    const handleModalSuccess = () => {
      Swal.fire({
        icon: "success",
        title: "Éxito",
        text: "Permisos actualizados correctamente.",
      });
      modalOpen.value = false;
      modalOpenCirculares.value = false;
      loadUsers(currentPage.value);
    };

    const openModalImportar = () => {
      console.log("Abrir modal para agregar un nuevo usuario");
      Swal.fire("Info", "Funcionalidad de Nuevo Usuario aquí", "info");
    };

    const debouncedFetchUsers = debounce(() => {
      loadUsers(1);
    }, 500);

    watch(searchTerm, () => {
      debouncedFetchUsers();
    });

    onMounted(() => {
      loadUsers();
    });

    return {
      users,
      searchTerm,
      currentPage,
      lastPage,
      loading,
      loadUsers,
      openViewsModal,
      openCircularModal,
      handleModalClose,
      handleModalSuccess,
      modalOpen,
      modalOpenCirculares,
      selectedUser,
      openModalImportar,
      totalUsers,
    };
  },
};
</script>

<style scoped>
.page-wrapper {
  background-color: #f8f9fa;
  min-height: 100vh;
}

.main-card {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.card-header-custom {
  padding: 1.5rem 2rem;
  background-color: white;
}

.card-footer-custom {
  background-color: #f8f9fa;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.text-monospace {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
}

.bg-primary-subtle {
  background-color: #cfe2ff !important;
  color: #084298 !important;
}

.fa-fw {
  width: 1.25em;
  text-align: center;
}

.table-responsive::-webkit-scrollbar {
  height: 8px;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .card-header-custom {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
